// ProductGrid — responsive grid of ProductCards.
// 1-col mobile (< 480px), 2-col tablet (480–767px), and from 768px up a
// width-driven grid that ADDS COLUMNS instead of stretching tiles.
// Inline styles only — responsive via a useMediaQuery hook.

import { useState, useEffect } from "react";
import { useEmbedTokens, EmbedBanner, EmbedLink } from "../../embed-shared/primitives";
import type { Product } from "../../embed-shared/types";
import { ProductCard } from "./ProductCard";

/**
 * Operator item (p), 2026-08-16: "the product tiles should not be limited to
 * 3 [columns], otherwise, as browser screen expands, the product tiles
 * becomes overly huge and not visually accurate".
 *
 * The desktop branch was a hard `repeat(3, 1fr)` for EVERY width >= 768px.
 * Because the tracks were `1fr`, every extra pixel of viewport was divided
 * among exactly three tiles — on a 2560px screen each tile was ~800px wide.
 * The column count is now derived by the browser from the available width, so
 * width buys COLUMNS rather than tile size, and the magic 3 is gone.
 *
 * Why `minmax(MIN, 1fr)` and not `minmax(MIN, MAXpx)`, which is the more
 * obvious way to spell "tiles stop growing":
 *
 *   1. A definite max INVERTS the column count. Per CSS Grid §7.2.3.1 the
 *      auto-fill repetition count is computed from the track's MAXIMUM sizing
 *      function when that is definite. `minmax(260px, 340px)` therefore packs
 *      by 340px, not 260px, yielding FEWER columns than today at common
 *      widths — the opposite of what this item asks for.
 *   2. A definite max cannot consume the remainder, so every width leaves up
 *      to a full track of dead space. Pooled at one edge (justify-content:
 *      start) that reads as a broken right margin against the full-width hero
 *      and toolbar above; split (center) it misaligns the grid's left edge
 *      from every other band on the page. There is no good answer, only two
 *      visible ones.
 *
 * `1fr` as the max keeps the count driven by MIN and lets the tracks absorb
 * the remainder exactly, so the row always fills its container — no leftover
 * to place, hence no justify-content decision to get wrong. Tile growth is
 * still bounded, because a track can never reach 2xMIN: at that point another
 * whole column fits and the row re-flows.
 *
 * Measured in Chromium at this MIN (container width -> columns x tile):
 *   720 -> 2 x 348   976 -> 3 x 309   1232 -> 4 x 290   1392 -> 4 x 330
 *  1552 -> 5 x 291  1872 -> 6 x 292   2512 -> 8 x 293   3792 -> 13 x 270
 * i.e. a 270–348px band the whole way from 768px to 4K, versus ~821px at the
 * 2512 case before this change. That band IS the "sensible maximum" this item
 * asks for; there is no separate cap to keep in sync.
 *
 * MIN is the knob: raise it for fewer/larger tiles, lower it for more/smaller.
 */
const GRID_MIN_COLUMN_WIDTH = 260;

/**
 * Viewport width at/above which the grid switches to the width-driven track
 * list above. Below this the explicit ladder still applies, deliberately: the
 * 1-col (<480) and 2-col (480–767) steps are load-bearing small-screen
 * behavior, and letting auto-fill derive them from a 260px MIN would change
 * both. The defect this item fixes only exists on wide screens, so only wide
 * screens change.
 */
const GRID_FLUID_MIN_VIEWPORT = 768;
/** Tablet ladder step: 2 columns from here up to GRID_FLUID_MIN_VIEWPORT. */
const GRID_TWO_COLUMN_MIN_VIEWPORT = 480;

/**
 * The grid's `grid-template-columns` for a given viewport width.
 *
 * Exported so the co-located test asserts against this function rather than
 * restating the track list as a literal (§55), and so the loading skeleton and
 * the real grid provably cannot drift apart — they were two independent
 * copies of the same template string before this item.
 */
export function productGridColumns(width: number): string {
  if (width >= GRID_FLUID_MIN_VIEWPORT) {
    return `repeat(auto-fill, minmax(${GRID_MIN_COLUMN_WIDTH}px, 1fr))`;
  }
  const columns = width >= GRID_TWO_COLUMN_MIN_VIEWPORT ? 2 : 1;
  return `repeat(${columns}, minmax(0, 1fr))`;
}

// NOT the shared embed-shared/use-container-width.ts hook of the same name
// (final whole-branch review, I-5) — this one is module-private to this file
// and returns window.innerWidth (VIEWPORT width via a resize listener), not
// a measured CONTAINER width via ResizeObserver. Ruling: disambiguate here,
// do not rename — renaming a module-private hook in this file is scope creep
// into a file no task touched. Use the shared hook (embed-shared/
// use-container-width.ts) for layout that should track its own container,
// not the viewport.
function useContainerWidth(): number {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

export interface ProductGridProps {
  products: Product[];
  loading?: boolean;
  error?: string | null;
  /**
   * True when the empty `products` list is the result of an active search or
   * category filter, rather than a genuinely empty catalog (Task 17). Lets
   * the empty state tell "no results for your filters" apart from "this
   * store has nothing for sale" — the two need different copy and only the
   * former offers a way back.
   */
  filtered?: boolean;
  /** Resets the active filters. Rendered as a "Clear filters" action in the
   *  filtered-empty state; omitted (no button) if not provided. */
  onClearFilters?: () => void;
  onProductClick?: (product: Product) => void;
}

export function ProductGrid({
  products,
  loading,
  error,
  filtered,
  onClearFilters,
  onProductClick,
}: ProductGridProps) {
  const t = useEmbedTokens();
  const width = useContainerWidth();
  const gridColumns = productGridColumns(width);

  if (loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          display: "grid",
          gridTemplateColumns: gridColumns,
          gap: t.spacing.lg,
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            aria-hidden="true"
            style={{
              display: "flex",
              flexDirection: "column",
              borderRadius: t.borderRadiusLg,
              backgroundColor: t.colors.surfaceSunken,
              overflow: "hidden",
            }}
          >
            {/*
              Media region sized to match ProductCard's 4:3 media area. Uses
              padding-top (universal — works in pre-2021 browsers without
              aspect-ratio support, and the embed's browser floor is set by
              CUSTOMER sites) rather than aspect-ratio, so the skeleton tracks
              the real card's media height everywhere. A fixed 280px here used
              to under-approximate the ~360px 4:3 card, INCREASING layout shift
              when loading resolved (TD-313).
            */}
            <div style={{ width: "100%", paddingTop: "75%", flexShrink: 0 }} />
            {/* Content stub approximating the h3 + price row area. */}
            <div style={{ height: 96, flexShrink: 0 }} />
          </div>
        ))}
        <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)" }}>
          Loading products...
        </span>
      </div>
    );
  }

  if (error) {
    return <EmbedBanner tone="error">{error}</EmbedBanner>;
  }

  if (products.length === 0) {
    return (
      <div
        role="status"
        style={{
          padding: t.spacing.xl,
          textAlign: "center",
          color: t.colors.textSecondary,
          fontSize: t.fontSize.md,
        }}
      >
        {filtered ? (
          <>
            <p style={{ margin: `0 0 ${t.spacing.md}px 0` }}>No products match your filters.</p>
            {onClearFilters && (
              <EmbedLink onClick={onClearFilters}>Clear filters</EmbedLink>
            )}
          </>
        ) : (
          "No products available."
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: gridColumns,
        gap: t.spacing.lg,
      }}
    >
      {products.map((p) => (
        <ProductCard key={p.id} product={p} onClick={onProductClick} />
      ))}
    </div>
  );
}
